import { connect } from '@cerebral/react'
import { Box, Button, Grid, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { props, sequences, state } from 'cerebral'
import PropTypes from 'prop-types'
import { mergeDeepRight, mergeRight } from 'ramda'
import React, { useCallback, useEffect } from 'react'
import { camelize, capitalize } from 'underscore.string'
import appModules from '../../lib/util/app-modules'
import { mergePropsFactory, reactionLifecycle } from '../../lib/util/cerebral'
import { routePaths } from '../../modules/route'
import DropDown from '../controls/DropDown'
import ListPagination from '../controls/ListPagination'
import ActionHeader from '../elements/ActionHeader'
import ActionScreen from '../elements/ActionScreen'
import ExportCSV from '../elements/CSVExport'
import LoadingScreen from '../elements/LoadingScreen'
import FilterBy from './FilterBy'
import Search from './Search'

const useStyles = makeStyles((theme) => ({
  actions: {
    width: 60,
    paddingRight: 10,
  },
  groupRow: {
    backgroundColor: theme.palette.grey['200'],
  },
  sticky: {
    position: 'sticky',
    right: 0,
    backgroundColor: theme.palette.background.paper,
  },
}))

export const TableListRow = ({ actions = [], children, className, sticky = false }) => {
  const classes = useStyles()
  return (
    <TableRow hover className={className}>
      {children}
      <TableCell align="right" className={`${classes.actions} ${sticky ? classes.sticky : ''}`}>
        {actions.length > 0 && <DropDown>{actions}</DropDown>}
      </TableCell>
    </TableRow>
  )
}

TableListRow.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.node),
  children: PropTypes.node,
  className: PropTypes.string,
  sticky: PropTypes.bool,
}

const mergeProps = mergePropsFactory(mergeRight)

const TableList = connect(
  {
    find: sequences`${props`type`}s.findForList`,
    list: state`${props`type`}s.${props`listKey`}`,
    data: state`${props`type`}s.${props`listKey`}.data`,
  },

  mergeProps,

  ({
    title,
    type,
    typeDisplay,
    category,
    find: originalFind,
    findParams = {},
    refreshMs = 0,
    listKey,
    list,
    data,
    intro,
    actionButtons,
    head,
    emptyContent,
    emptyScreenProps,
    group,
    itemComponent,
    create = true,
    createHref,
    search,
    searchType,
    searchExtra,
    filter,
    reaction,
    size = 'small',
    allowExport, // NOTE: This currently only has an effect if head is an array.
    exportOptions,
    headerActions, // NOTE: Like allowExport, this only renders if head is an array.
    tableContainerProps,
  }) => {
    const find = useCallback((params) => originalFind(mergeDeepRight(params, { ...findParams, listKey })), [originalFind, findParams, listKey])
    reactionLifecycle(reaction, `routeChange`, { route: state`route` }, () => find())
    const classes = useStyles()
    const { loading = true } = list
    const routePath = routePaths[`to${capitalize(camelize(type))}`]
    const appModule = appModules[type] || {}
    createHref = createHref || (routePath && routePath({ id: 'create' }))
    typeDisplay = typeDisplay || appModule.displayType || type
    const pluralType = appModule.plural || `${typeDisplay}s`
    const hasFilterOptions = filter?.options?.length > 0
    const showExport = allowExport && exportOptions && data.length > 0
    let lastGroupBy = null
    const hasHeaderActions = Boolean(headerActions || showExport)

    useEffect(() => {
      if (refreshMs) {
        const interval = setInterval(find, refreshMs)
        return () => clearInterval(interval)
      }
    }, [refreshMs, find])

    if (Array.isArray(head)) {
      head = (
        <TableRow>
          {head.map((title, index) => (
            <TableCell key={title.key || title || index}>
              <Typography variant="h6">{title}</Typography>
            </TableCell>
          ))}
          {/* If more table-level actions are added, we can replace this with a dots menu. */}
          <TableCell key="table-actions" className={`${classes.actions} ${hasHeaderActions ? classes.sticky : ''}`}>
            <Box display="flex" alignItems="center" justifyContent="flex-end">
              {headerActions}
              {showExport && <ExportCSV options={exportOptions} type={type} />}
            </Box>
          </TableCell>
        </TableRow>
      )
    }

    return (
      <Box pb={6}>
        <ActionHeader title={title || appModule.moduleTitle}>
          {actionButtons}
          {create && createHref && (
            <Button component={Link} color="primary" href={createHref}>
              Add {typeDisplay}
            </Button>
          )}
        </ActionHeader>
        {Boolean(intro || search || hasFilterOptions || searchExtra) && (
          <Grid container alignItems="center">
            <Grid item style={{ flexGrow: 1 }}>
              {intro && <Box my={2}>{intro}</Box>}
            </Grid>
            {search && (
              <Grid item md={3}>
                <Box my={2} ml={2}>
                  <Search placeholder={`Filter ${category ? category + ' ' : ''}${searchType || pluralType}`} />
                </Box>
              </Grid>
            )}
            {searchExtra && <Grid item>{searchExtra}</Grid>}
            {hasFilterOptions && (
              <Grid item md={2}>
                <Box my={2} ml={2}>
                  <FilterBy {...filter} />
                </Box>
              </Grid>
            )}
          </Grid>
        )}
        <Box my={4}>
          {data.length === 0 && !loading ? (
            <ActionScreen title={emptyContent || `There are no ${category || ''} ${pluralType}.`} color="muted" {...emptyScreenProps}>
              {create && createHref && (
                <Button component={Link} color="primary" href={createHref}>
                  Add {typeDisplay}
                </Button>
              )}
            </ActionScreen>
          ) : (
            <TableContainer component={Paper} {...tableContainerProps}>
              <Table size={size}>
                <TableHead>{head}</TableHead>
                <TableBody>
                  {/* The loading screen causes a significant document reflow when refresh is active. */}
                  {loading && !refreshMs ? (
                    <TableRow>
                      <TableCell colSpan={100}>
                        <LoadingScreen />
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.filter(Boolean).map((item) => {
                      const { _id: id } = item
                      const Component = itemComponent
                      let output = <Component key={id} type={type} id={id} />
                      if (group) {
                        const { by, component: Group } = group
                        const groupBy = by(item)
                        if (lastGroupBy !== groupBy) {
                          output = [
                            <TableRow key={`group-${id}`}>
                              <TableCell classes={{ root: classes.groupRow }} colSpan={100}>
                                <Group item={item}></Group>
                              </TableCell>
                            </TableRow>,
                            output,
                          ]
                        }
                        lastGroupBy = groupBy
                      }
                      return output
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <ListPagination list={list} find={find} />
      </Box>
    )
  }
)

TableList.propTypes = {
  title: PropTypes.string,
  type: PropTypes.string.isRequired,
  size: PropTypes.string,
  itemComponent: PropTypes.elementType.isRequired,
  listKey: PropTypes.string,
  search: PropTypes.bool,
  searchExtra: PropTypes.node,
  group: PropTypes.shape({
    by: PropTypes.func,
    component: PropTypes.elementType,
  }),
  actionButtons: PropTypes.node,
  allowExport: PropTypes.bool,
  exportOptions: PropTypes.shape({
    formatter: PropTypes.func,
    filename: PropTypes.string,
    query: PropTypes.object,
  }),
  headerActions: PropTypes.node,
  stickyOptions: PropTypes.bool,
  emptyScreenProps: PropTypes.object,
}

TableList.defaultProps = {
  listKey: 'list',
}

export default TableList
